import React, { useState } from 'react';
import { User, MapPin, Mail, ShoppingCart, Star, TrendingUp, Users, DollarSign, Package as PkgIcon } from 'lucide-react';
import {
  Skeleton, registerBlueprints,
  userCardBlueprints, productCardBlueprints, dashboardStatBlueprints, articlePreviewBlueprints,
} from '@/lib/ghostlayout';

// Register blueprints for playground
registerBlueprints({
  'user-card': userCardBlueprints,
  'product-card': productCardBlueprints,
  'dashboard-stat': dashboardStatBlueprints,
  'article-preview': articlePreviewBlueprints,
});

function Toggle({ label, options, value, onChange, testId }) {
  return (
    <div>
      <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-white/20 block mb-2">{label}</span>
      <div className="flex border border-white/[0.08] rounded-md overflow-hidden">
        {options.map((opt) => (
          <button
            key={String(opt.value)}
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 text-xs font-medium transition-all ${value === opt.value ? 'bg-white text-black' : 'text-white/40 hover:text-white/60 hover:bg-white/[0.04]'}`}
            data-testid={`${testId}-${String(opt.value)}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function DemoUserCard() {
  return (
    <div className="p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center"><User className="w-5 h-5 text-white/40" /></div>
        <div><h3 className="text-sm font-bold">Sarah Johnson</h3><p className="text-xs text-white/40">Product Designer</p></div>
      </div>
      <div className="space-y-1.5 text-xs text-white/40">
        <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" />San Francisco, CA</div>
        <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" />sarah@example.com</div>
      </div>
      <button className="mt-3 w-full py-2 bg-white text-black text-xs font-semibold rounded-sm">View Profile</button>
    </div>
  );
}

function DemoProductCard() {
  return (
    <div>
      <div className="h-40 bg-gradient-to-br from-white/10 to-white/[0.02]" />
      <div className="p-4">
        <div className="flex gap-0.5 mb-1.5">{[1,2,3,4,5].map(i => <Star key={i} className="w-3 h-3 text-amber-400 fill-amber-400" />)}<span className="text-[10px] text-white/30 ml-1">(124)</span></div>
        <h3 className="text-sm font-bold mb-1">Premium Headphones</h3>
        <p className="text-[11px] text-white/30 mb-3">Noise cancellation</p>
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold">$299</span>
          <button className="px-3 py-1.5 bg-white text-black text-xs font-semibold rounded-sm flex items-center gap-1"><ShoppingCart className="w-3 h-3" />Add</button>
        </div>
      </div>
    </div>
  );
}

export default function PlaygroundPage() {
  const [loading, setLoading] = useState(true);
  const [animation, setAnimation] = useState('shimmer');
  const [theme, setTheme] = useState('dark');
  const [debug, setDebug] = useState(false);

  return (
    <div className="max-w-5xl" data-testid="playground-page">
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Playground</h1>
      <p className="text-base text-white/50 leading-relaxed mb-8">Toggle controls to see skeletons in action.</p>

      {/* Controls */}
      <div className="flex flex-wrap gap-4 mb-8 p-4 border border-white/[0.06] rounded-lg bg-[#0A0A0A]" data-testid="playground-controls">
        <Toggle label="State" options={[{ value: true, label: 'Loading' }, { value: false, label: 'Content' }]} value={loading} onChange={setLoading} testId="pg-state" />
        <Toggle label="Animation" options={[{ value: 'shimmer', label: 'Shimmer' }, { value: 'pulse', label: 'Pulse' }, { value: 'none', label: 'None' }]} value={animation} onChange={setAnimation} testId="pg-animation" />
        <Toggle label="Theme" options={[{ value: 'dark', label: 'Dark' }, { value: 'light', label: 'Light' }]} value={theme} onChange={setTheme} testId="pg-theme" />
        <Toggle label="Debug" options={[{ value: false, label: 'Off' }, { value: true, label: 'On' }]} value={debug} onChange={setDebug} testId="pg-debug" />
      </div>

      {/* Demo grid */}
      <div className="space-y-8">
        <div>
          <h3 className="text-sm font-bold mb-3 text-white/60">User Cards</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.04] border border-white/[0.06] rounded-lg overflow-hidden">
            {[0, 1, 2].map(i => (
              <div key={i} className={theme === 'light' ? 'bg-white text-black' : 'bg-[#0A0A0A]'} data-skeleton-name="user-card">
                <Skeleton name="user-card" loading={loading} animation={animation} theme={theme} debug={debug} minHeight={200}>
                  <DemoUserCard />
                </Skeleton>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-bold mb-3 text-white/60">Product Cards</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.04] border border-white/[0.06] rounded-lg overflow-hidden">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className={theme === 'light' ? 'bg-white text-black' : 'bg-[#0A0A0A]'} data-skeleton-name="product-card">
                <Skeleton name="product-card" loading={loading} animation={animation} theme={theme} debug={debug} minHeight={320}>
                  <DemoProductCard />
                </Skeleton>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-bold mb-3 text-white/60">Dashboard Stats</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/[0.04] border border-white/[0.06] rounded-lg overflow-hidden">
            {[
              { icon: Users, label: 'USERS', value: '12,345', change: '+12.5%' },
              { icon: DollarSign, label: 'REVENUE', value: '$45,678', change: '+8.2%' },
              { icon: PkgIcon, label: 'ORDERS', value: '1,234', change: '+23.1%' },
              { icon: TrendingUp, label: 'GROWTH', value: '32.5%', change: '+5.4%' },
            ].map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className={theme === 'light' ? 'bg-white text-black' : 'bg-[#0A0A0A]'} data-skeleton-name="dashboard-stat">
                  <Skeleton name="dashboard-stat" loading={loading} animation={animation} theme={theme} debug={debug} minHeight={150}>
                    <div className="p-5">
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 rounded-md bg-white/10 flex items-center justify-center"><Icon className="w-5 h-5 text-white/40" /></div>
                        <span className="text-xs font-mono text-emerald-400">{stat.change}</span>
                      </div>
                      <p className="text-[10px] font-mono uppercase tracking-wider text-white/30 mb-0.5">{stat.label}</p>
                      <p className="text-xl font-bold">{stat.value}</p>
                    </div>
                  </Skeleton>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
